import React, {
  InputHTMLAttributes,
  forwardRef,
  useMemo,
  useState,
} from 'react'
import { cn } from '../../utils/cn'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  showPassword?: boolean
  onTogglePassword?: () => void
  tooltip?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      showPassword,
      onTogglePassword,
      type = 'text',
      disabled,
      max,
      tooltip,
      ...props
    },
    ref
  ) => {
    const [showTooltip, setShowTooltip] = useState(false)

    const baseClasses = cn(
      'w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border transition-all duration-200 ease-in-out',
      'text-sm sm:text-base font-normal placeholder-gray-400',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
      !error && !disabled && 'border-gray-300 hover:border-gray-400',
      error && 'border-red-500 focus:ring-red-500',
      disabled && 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
    )

    const labelClasses = cn(
      'text-sm font-medium',
      !error && !disabled && 'text-gray-700',
      error && 'text-red-600',
      disabled && 'text-gray-500'
    )

    const inputType = type === 'password' && showPassword ? 'text' : type

    const maxDate = useMemo(() => {
      if (type === 'date' && !max) {
        const today = new Date()
        const eighteenYearsAgo = new Date(
          today.getFullYear() - 18,
          today.getMonth(),
          today.getDate()
        )
        return eighteenYearsAgo.toISOString().split('T')[0]
      }
      return max
    }, [type, max])

    const handleTooltipToggle = () => {
      setShowTooltip(!showTooltip)
    }

    const handleTooltipClose = () => {
      setShowTooltip(false)
    }

    return (
      <div className="w-full">
        {label && (
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <label className={labelClasses}>{label}</label>
            {tooltip && (
              <div className="relative flex items-center">
                <svg
                  className="w-4 h-4 text-gray-400 cursor-pointer"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  onClick={handleTooltipToggle}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                {showTooltip && (
                  <>
                    {/* Overlay para cerrar tooltip en móvil */}
                    <div
                      className="fixed inset-0 z-40 sm:hidden"
                      onClick={handleTooltipClose}
                    />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-xs rounded-md py-2 px-3 z-50 transition-opacity duration-200">
                      {tooltip}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        <div className="relative">
          <input
            ref={ref}
            type={inputType}
            className={cn(baseClasses, className)}
            disabled={disabled}
            max={type === 'date' ? maxDate : max}
            {...props}
          />
          {type === 'password' && (
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={onTogglePassword}
            >
              {showPassword ? (
                <img
                  src="/assets/eye.svg"
                  alt="OLR Leasing"
                  className={`size-5`}
                />
              ) : (
                <img
                  src="/assets/eye-off.svg"
                  alt="OLR Leasing"
                  className={`size-5`}
                />
              )}
            </button>
          )}
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </div>
    )
  }
)

Input.displayName = 'Input'
